import { useState, useEffect, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faCircleChevronRight,
  faCircleChevronLeft,
  faPrint,
  faRefresh,
  faCheck,
  faUserPlus,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { getUsers, syncClientInformation, updateClientUser } from "../../redux/actions/users";
import { useDispatch, useSelector } from "react-redux";
import { ablToken, authanticatedUser, branches, capitalizeFirstLetter, formatTime } from "../../utils/helper";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function ClientsTables({ options }) {
  const dispatch = useDispatch();
  const { users, loading } = useSelector((state) => state.users);
  const [currentPage, setCurrentPage] = useState(1);
  const [fullName, setFullName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [status, setStatus] = useState("");
  const [branch, setBranch] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [format, setFormat] = useState("");
  const itemsPerPage = 50;
  const tableRef = useRef(null);



  const statuses = [
    { id: "", name: "All" },
    { id: "approved", name: "Approve" },
    { id: "pending", name: "Pending" },
  ];

  useEffect(() => {
    dispatch(getUsers({ token: authanticatedUser?.token }));
  }, [dispatch]);


  const resetFilter = () => {
    setFullName("");
    setAccountNumber("");
    setStatus("");
    setBranch("");
    setDateRange({ startDate: null, endDate: null });
    setFormat("");
  };

  const allUsers = useMemo(() => {
    if (!Array.isArray(users)) {
      toast.error("Invalid users data provided.");
      return [];
    }

    let filtered = users;

    if (fullName) {
      filtered = filtered.filter((user) =>
        user?.name?.toLowerCase().startsWith(fullName.toLowerCase())
      );
    }

    if (accountNumber) {
      filtered = filtered.filter((user) =>
        user?.username?.toLowerCase().startsWith(accountNumber.toLowerCase())
      );
    }

    if (status) {
      filtered = filtered.filter(
        (user) => user?.status?.toLowerCase() === status.toLowerCase()
      );
    }

    if (branch) {
      filtered = filtered.filter(
        (user) =>
          user?.createdBy?.branch?.toLowerCase() === branch.toLowerCase()
      );
    }

    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate).setHours(0, 0, 0, 0);
      const end = new Date(dateRange.endDate).setHours(23, 59, 59, 999);
      filtered = filtered.filter((user) => {
        const userDate = user?.createdAt ? new Date(user.createdAt).getTime() : null;
        return userDate && userDate >= start && userDate <= end;
      });
    }

    return filtered;
  }, [users, fullName, accountNumber, status, branch, dateRange]);

  const { toggleCreateClientModal, toggleEditClientModal } = options || {};

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = allUsers.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(allUsers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    let startPage = Math.max(1, currentPage - 4);
    let endPage = Math.min(totalPages, startPage + 9);

    if (endPage - startPage < 9) {
      startPage = Math.max(1, endPage - 9);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  const exportClientToPDF = (user) => {
    if (!user) {
      toast.error("Invalid user data for PDF export.");
      return;
    }

    const fullTableHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>AccessBank Online Banking Registration Form</title>
    <style>
        body {
            font-family: Arial, sans-serif;
        }
        .container {
            width: 80%;
            margin: auto;
            padding: 20px;
        }
        .header {
            text-align: center;
            margin-bottom: 20px;
        }
        .header img {
            width: 150px;
        }
        .form-section {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin-bottom: 20px;
        }
        .form-section label {
            display: flex;
            margin-bottom: 5px;
        }
        .terms {
            margin-top: 20px;
        }
        .signatures {
            margin-top: 20px;
        }
        .signatures div {
            margin-bottom: 10px;
        }
        p {
          font-size: 13px;
        }
        ol li {
          font-size: 13px;
          margin-bottom: 10px;
        }
        label {
          font-size: 12px;
        }
        ol {
          margin-top: 10px;
          margin-left: 20px
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <img src="/assets/ablLogo2.png" alt="AccessBank Liberia - ACCESS GROUP" />
            <h2>Online Banking Registration Form</h2>
        </div>
        <br />
        <div class="form-section">
            <div>
              <label for="branch">Branch Name: ${user?.createdBy?.branch || "N/A"}</label>
              <label for="customerNumber">Customer Number: ${user?.clientNumber || "N/A"}</label>
              <label for="customerName">Customer Name: ${user?.name || "N/A"}</label>
            </div>
            <div>
              <label for="date">Registration Date: ${
                user?.createdAt ? formatTime(user.createdAt) : "N/A"
              }</label>
              <label for="mobileNumber">Mobile Number: ${user?.phone || "N/A"}</label>
              <label for="idNumber">ID Number: ${user?.ids || "N/A"}</label>
            </div>
        </div>
        <div class="terms">
            <h3>Terms and Conditions:</h3>
            <p>I/We apply to be onboarded for AccessBank Online Banking Services and authorize the use of my information by AccessBank Liberia to link my account(s). I/We confirm that all information provided is accurate and shall inform AccessBank of any changes. AccessBank may verify my/our information from any sources deemed appropriate.</p>
            <p>By signing, I/We agree to the following terms:</p>
            <ol>
                <li>1. This application is for AccessBank Online Banking services for my/our personal use.</li>
                <li>2. AccessBank may decline this application without providing reasons.</li>
                <li>3. I/We agree not to engage in unauthorized activities that may breach security protocols or access unauthorized data.</li>
                <li>4. I/We shall cease using the Online Banking services upon notification of its cancellation.</li>
                <li>5. In case of unauthorized access or a compromised account, I/We shall promptly inform AccessBank. Failure to do so may increase risks for which AccessBank is not liable.</li>
                <li>6. I/We shall not disclose my/our login credentials, including username and password, to anyone, including family, colleagues, or AccessBank staff, and assume responsibility if I/we do.</li>
                <li>7. My/Our access credentials shall be used solely for Online Banking services.</li>
                <li>8. AccessBank may debit my/our account for fees related to the use of Online Banking services.</li>
                <li>9. AccessBank may terminate services at any time without prior notice or liability.</li>
                <li>10. AccessBank reserves the right to amend these terms at its discretion without notice.</li>
                <li>11. These terms are governed by the laws of the Republic of Liberia.</li>
            </ol>
        </div>
        <div class="signatures">
            <div style="display: flex; flex-direction: row; align-item: center;">
                <label for="clientSignature">Client's Signature:</label>
                 <p>____________________________________</p>
            </div>
            <div>
                <label for="advisorName">Client Advisor's Name and Signature:</label>
                 <p>____________________________________</p>
            </div>
            <div>
                <label for="supervisorName">Supervisor's Name and Signature:</label>
              <p>____________________________________</p>
            </div>
        </div>
    </div>
</body>
</html>`;

    const element = document.createElement("div");
    element.innerHTML = fullTableHtml;

    const options = {
      margin: 10,
      filename: `Terms_and_Conditions_${user?.username || "client"}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };

    html2pdf().from(element).set(options).save();
  };

  const exportToPDF = () => {
    if (!allUsers.length) {
      toast.error("No users available to export.");
      return;
    }

    const fullTableHtml = `
      <div style="width: 100%; padding: 50px; margin-bottom: 50px;">
        <h1 style="font-size: 24px; font-weight: bold;">Customers</h1>
        <div style="float: right; text-align: right;">
          <p>Date Range: ${
            dateRange.startDate && dateRange.endDate
              ? `${formatTime(dateRange.startDate)} - ${formatTime(dateRange.endDate)}`
              : "All"
          }</p>
          <p>Status: ${status ? status.toUpperCase() : "All"}</p>
          <p>Branch: ${branch ? branch.toUpperCase() : "All"}</p>
        </div>
        <div style="width: 100%; height: 50px;"></div>
        <table border="1" style="width: 100%; border-collapse: collapse; border-width: 1px; margin-top: 50px;">
          <thead>
            <tr style="border-width: 1px;">
              <th>Name</th>
              <th>Username</th>
              <th>Mobile</th>
              <th>Client Number</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${allUsers
              .map(
                (user, i) => `
              <tr>
                <td style="font-size: 13px;">${user?.name || "N/A"}<br>${
                  user?.email || "N/A"
                }</td>
                <td style="font-size: 13px;">${user?.username || "N/A"}</td>
                <td style="font-size: 13px;">${user?.phone || "N/A"}</td>
                <td style="font-size: 13px;">${
                  user?.clientNumber || user?.username || "N/A"
                }</td>
                <td style="font-size: 13px;">${user?.status || "N/A"}</td>
                <td style="font-size: 13px;">${
                  user?.createdAt ? formatTime(user.createdAt) : "N/A"
                }</td>
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </div>
    `;

    const element = document.createElement("div");
    element.innerHTML = fullTableHtml;

    const options = {
      margin: 10,
      filename: "Customers.pdf",
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    html2pdf().from(element).set(options).save();
  };

  const exportToExcel = () => {
    if (!allUsers.length) {
      toast.error("No users available to export.");
      return;
    }

    const newArray = allUsers.map((user, i) => ({
      ID: i + 1,
      Name: user?.name || "N/A",
      Email: user?.email || "N/A",
      Username: user?.username || "N/A",
      Mobile: user?.phone || "N/A",
      "Client Number": user?.clientNumber || user?.username || "N/A",
      Status: user?.status || "N/A",
      "Registration Date": user?.createdAt ? formatTime(user.createdAt) : "N/A",
      Branch: user?.createdBy?.branch || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(newArray);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");
    XLSX.writeFile(wb, "Customers.xlsx");
  };

  const syncClientInfo = (client) => {
    if (!client?.username) {
      toast.error("Invalid client data for syncing.");
      return;
    }
    const tmpData = {
      ablToken,
      clientNumber: client.username,
      token: authanticatedUser?.token,
    };
    dispatch(syncClientInformation(tmpData));
  };

  const approveClient = (client) => {
    if (!client?._id) {
      toast.error("Invalid client data for approval.");
      return;
    }
    const tmpData = {
      status: "approved",
      ablToken,
      id: client._id,
      token: authanticatedUser?.token,
    };
    dispatch(updateClientUser(tmpData));
  };

  return (
    <div className="flex flex-wrap ">
      <div className="mt- mb-5 mx-3 px-3 py-10 w-full bg-white border-0 border-transparent border-solid shadow-soft-xl rounded-2xl bg-clip-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-end">
          <div className="lg:col-span-4 mb-2">
            <label
              htmlFor="fullName"
              className="block text-xl font-bold text-primary-dark"
            >
              Full Name
            </label>
            <div className="mt-2">
              <input
                id="fullName"
                name="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="block w-full pl-5 py-3 rounded-md border-0 text-primary shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="lg:col-span-4 mb-2">
            <label
              htmlFor="accountNumber"
              className="block text-xl font-bold text-primary-dark"
            >
              Account Number
            </label>
            <div className="mt-2">
              <input
                id="accountNumber"
                name="accountNumber"
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                className="block w-full pl-5 py-3 rounded-md border-0 text-primary shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="lg:col-span-2 mb-2">
            <label
              htmlFor="status"
              className="block text-xl font-bold text-primary-dark mb-2"
            >
              Status
            </label>
            <div className="relative">
              <select
                id="status"
                name="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
              >
                {statuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>

          <br />

          <div className="lg:col-span-4 mb-2">
            <label
              htmlFor="branch"
              className="block text-xl font-bold text-primary-dark mb-2"
            >
              Branch
            </label>
            <div className="relative">
              <select
                id="branch"
                name="branch"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="block appearance-none w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-200"
              >
                <option value="">All Branches</option>
                {branches.map((branch) => (
                  <option key={branch} value={branch}>{capitalizeFirstLetter(branch + ' ')} branch </option> 
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg
                  className="fill-current h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2 mb-2">
            <label
              htmlFor="dateRange"
              className="block text-xl font-bold text-primary-dark mb-2"
            >
              Date Range
            </label>
            <DatePicker
              selectsRange
              startDate={dateRange.startDate ? new Date(dateRange.startDate) : null}
              endDate={dateRange.endDate ? new Date(dateRange.endDate) : null}
              onChange={(dates) => {
                const [start, end] = dates;
                console.log("DatePicker selected:", { start, end });
                setDateRange({ startDate: start, endDate: end });
              }}
              className="w-full pl-5 py-3 rounded-md border border-gray-300 text-primary shadow-sm focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
            />
          </div>
          <div className="lg:col-span-2 flex flex-col sm:flex-row justify-between items-stretch space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              type="button"
              className="px-4 py-2 font-bold text-center bg-red-700 uppercase rounded-lg text-white text-xs shadow-md hover:shadow-lg transition-all duration-200 flex-grow"
              onClick={() => resetFilter()}
            >
              <FontAwesomeIcon icon={faRefresh} className="mr-2" />
              Reset Filter
            </button>
            {authanticatedUser?.role?.toLowerCase() === 'iso' ? '' : <> 
              {authanticatedUser?.accessModules?.[0]?.rights?.includes("create") ? (
                <button
                  type="button"
                  className="px-4 py-2 font-bold text-center bg-gradient-to-tl from-brand-5 to-primary uppercase rounded-lg text-white text-xs shadow-md hover:shadow-lg transition-all duration-200 flex-grow"
                  onClick={() => toggleCreateClientModal?.()}
                >
                  <FontAwesomeIcon icon={faUserPlus} className="mr-2" style={{ fontSize: "16px" }} />
                  Create Client
                </button>
              ) : null}
              </>
            }
            <select
              id="downloadFormat"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
            >
              <option value="">Select Format</option>
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
            </select>
            <button
              onClick={format === "excel" ? exportToExcel : exportToPDF}
              disabled={!format}
              className="text-white bg-primary hover:bg-primary focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download
            </button>
          </div>
        </div>
      </div>
      <div className="flex-none w-full max-w-full px-3">
        <div className="relative flex flex-col min-w-0 mb-6 break-words bg-white border-0 border-transparent border-solid shadow-soft-xl rounded-2xl bg-clip-border">
          <div className="p-6 pb-0 mb-6 bg-white text-primary text-4xl font-extrabold border-b-0 border-b-solid rounded-t-2xl border-b-transparent">
            <h1>Customers</h1>
          </div>
          <div className="flex-auto px-0 pt-0 pb-2">
            <div className="p-0 overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow-soft-xl">
                  <div className="max-h-[calc(10*3rem)] overflow-y-auto">
                    {loading ? (
                      <p className="text-center text-primary">Loading...</p>
                    ) : currentItems.length > 0 ? (
                      <table
                        ref={tableRef}
                        className="items-center min-w-full table-auto border-gray-200 text-slate-500"
                      >
                        <thead className="sticky top-0 bg-brand-2 z-10">
                          <tr>
                            <th className="px-6 py-3 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Name
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Username
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Mobile
                            </th>
                             <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Branch
                            </th>
                            <th className="px-6 py-3 pl-2 font-bold text-left uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Client Number
                            </th>
                            <th className="px-6 py-3 font-bold text-center uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Status
                            </th>
                            <th className="px-6 py-3 font-bold text-center uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Date
                            </th>
                            <th className="px-6 py-3 font-bold text-center uppercase align-middle bg-transparent border-b border-gray-200 shadow-none text-base border-b-solid tracking-none whitespace-nowrap text-white">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((client) => (
                            <tr key={client?._id || Math.random()}>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <div className="flex px-2 py-1">
                                  <div className="flex flex-col justify-center ml-4">
                                    <h6 className="mb-0 text-sm font-semibold text-primary">
                                      {client?.name || "N/A"}
                                    </h6>
                                    <p className="mb-0 text-xs text-brand-1">
                                      {client?.email || "N/A"}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {client?.username || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {client?.phone || "N/A"}
                                </p>
                              </td>

                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  { client?.createdBy?.branch || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                <p className="mb-0 text-sm font-semibold text-primary">
                                  {client?.clientNumber || client?.username || "N/A"}
                                </p>
                              </td>
                              <td className="p-2 text-sm text-center align-middle bg-transparent border-b whitespace-nowrap shadow-transparent">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded ${
                                    client?.status === "approved"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {client?.status || "N/A"}
                                </span>
                              </td>
                              <td className="p-2 text-sm text-center align-middle bg-transparent border-b whitespace-nowrap shadow-transparent">
                                {client?.createdAt ? formatTime(client.createdAt) : "N/A"}
                              </td>
                              {authanticatedUser?.role?.toLowerCase() === 'iso' ? '' : 
                                <td className="p-2 text-center align-middle bg-transparent border-b border-gray-300 whitespace-nowrap shadow-transparent">
                                  <div className="flex justify-center space-x-2">
                                    <button
                                      className="inline-flex items-center px-2 py-1 bg-brand-6 hover:bg-brand-7 text-white text-xs font-semibold rounded"
                                      onClick={() => exportClientToPDF(client)}
                                      disabled={loading}
                                    >
                                      <FontAwesomeIcon icon={faPrint} className="mr-1" />
                                      Terms
                                    </button>
                                    {authanticatedUser?.role?.toLowerCase() === 'iso' ? '' : <>
                                      {authanticatedUser?.accessModules?.[0]?.rights?.includes(
                                        "edit"
                                      ) ? (
                                        <button
                                          onClick={() => toggleEditClientModal?.(client)}
                                          className="inline-flex items-center px-2 py-1 bg-brand-9 hover:bg-brand-10 text-white text-xs font-semibold rounded"
                                          disabled={loading}
                                        >
                                          <FontAwesomeIcon icon={faEdit} className="mr-1" />
                                          Edit
                                        </button>
                                      ) : null}
                                    </>}


                                    {authanticatedUser?.accessModules?.[0]?.rights?.includes(
                                      "create"
                                    ) ? (
                                      <button
                                        onClick={() => syncClientInfo(client)}
                                        className="inline-flex items-center px-2 py-1 bg-orange-500 hover:bg-brand-10 text-white text-xs font-semibold rounded"
                                        disabled={loading}
                                      >
                                        <FontAwesomeIcon icon={faRefresh} className="mr-1" />
                                        Sync
                                      </button>
                                    ) : null}

                                    {client?.status !== "approved" &&
                                    authanticatedUser?.accessModules?.[0]?.rights?.includes(
                                      "approve"
                                    ) ? (
                                      <button
                                        onClick={() => approveClient(client)}
                                        className="inline-flex items-center px-2 py-1 bg-green-500 hover:bg-brand-10 text-white text-xs font-semibold rounded"
                                        disabled={loading}
                                      >
                                        <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                        Approve
                                      </button>
                                    ) : null}
                                  </div>
                                </td>
                              }
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-center text-primary">
                        {users?.length === 0
                          ? "No users available"
                          : "No users match the current filters"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full mt-4">
            <div className="flex flex-col sm:flex-row justify-between items-center p-4 bg-white rounded-lg shadow">
              <div className="text-sm font-semibold text-primary mb-2 sm:mb-0">
                Showing {indexOfFirstItem + 1} to{" "}
                {Math.min(indexOfLastItem, allUsers.length)} of {allUsers.length}{" "}
                results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`rounded-md p-1 ${
                    currentPage === 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-primary hover:bg-gray-100"
                  }`}
                >
                  <FontAwesomeIcon icon={faCircleChevronLeft} className="text-xl" />
                </button>
                <div className="flex space-x-1">
                  {getPageNumbers().map((number) => (
                    <button
                      key={number}
                      onClick={() => paginate(number)}
                      className={`px-3 py-1 rounded-md text-sm ${
                        currentPage === number
                          ? "bg-primary text-white"
                          : "bg-gray-200 text-primary hover:bg-gray-300"
                      }`}
                    >
                      {number}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`rounded-md p-1 ${
                    currentPage === totalPages
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-primary hover:bg-gray-100"
                  }`}
                >
                  <FontAwesomeIcon icon={faCircleChevronRight} className="text-xl" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

ClientsTables.propTypes = {
  options: PropTypes.shape({
    toggleCreateClientModal: PropTypes.func,
    toggleEditClientModal: PropTypes.func,
  }),
};

export default ClientsTables;